import {
  GatewayResolutionGraph,
  GraphEdge,
  GraphNode,
  GraphNodeType,
  ItemNodeData,
  PaymentTypeNodeData,
} from './gateway-resolution-graph.model';

/**
 * Pure element/geometry helpers for the gateway resolution graph (no Cytoscape import).
 * The shapes are structurally assignable to Cytoscape's ElementDefinition.
 */

export interface GraphElement {
  group: 'nodes' | 'edges';
  data: Record<string, unknown>;
  classes: string;
}

export type RingType = 'gateway' | 'paymentType' | 'accountingPackage' | 'item';

export const RING_LEVEL: Record<GraphNodeType, number> = {
  gateway: 4,
  paymentType: 3,
  accountingPackage: 2,
  item: 1,
  more: 1,
};

export const RING_OF: Record<GraphNodeType, RingType> = {
  gateway: 'gateway',
  paymentType: 'paymentType',
  accountingPackage: 'accountingPackage',
  item: 'item',
  more: 'item',
};

export const RING_LABEL_KEYS: Record<RingType, string> = {
  gateway: 'revenue.gateway-resolution-tree.graph-ring-gateways',
  paymentType: 'revenue.gateway-resolution-tree.graph-ring-payment-types',
  accountingPackage: 'revenue.gateway-resolution-tree.graph-ring-accounting-packages',
  item: 'revenue.gateway-resolution-tree.graph-ring-items',
};

/** PrimeIcons code points (primeicons 7.0.0): pi-tag, pi-folder, pi-credit-card, pi-cloud, pi-ellipsis-h. */
export const NODE_GLYPH: Record<GraphNodeType, string> = {
  item: '',
  accountingPackage: '',
  paymentType: '',
  gateway: '',
  more: '',
};

export const NODE_ICON_CLASS: Record<GraphNodeType, string> = {
  item: 'pi pi-tag',
  accountingPackage: 'pi pi-folder',
  paymentType: 'pi pi-credit-card',
  gateway: 'pi pi-cloud',
  more: 'pi pi-ellipsis-h',
};

export const MIN_NODE_SIZE = 20;
export const MAX_NODE_SIZE = 44;
export const NODE_SIZE_PER_EDGE = 3;
export const LAYOUT_BOX = { x1: 0, y1: 0, w: 1000, h: 1000 };
export const LAYOUT_CENTRE = { x: LAYOUT_BOX.x1 + LAYOUT_BOX.w / 2, y: LAYOUT_BOX.y1 + LAYOUT_BOX.h / 2 };

const RING_ORDER: RingType[] = ['gateway', 'paymentType', 'accountingPackage', 'item'];

export function nodeSize(degree: number): number {
  return Math.min(MAX_NODE_SIZE, MIN_NODE_SIZE + NODE_SIZE_PER_EDGE * Math.max(0, degree));
}

function defaultPathPairs(paths: string[][]): Set<string> {
  const pairs = new Set<string>();
  for (const path of paths) {
    for (let i = 0; i + 1 < path.length; i++) {
      pairs.add(`${path[i]}|${path[i + 1]}`);
      pairs.add(`${path[i + 1]}|${path[i]}`);
    }
  }
  return pairs;
}

/** Keys of the edges lying between consecutive default-path entries (undirected). */
export function defaultPathEdgeKeys(graph: GatewayResolutionGraph, extraPaths: string[][] = []): Set<string> {
  const pairs = defaultPathPairs([graph.defaultPath, ...extraPaths]);
  const keys = new Set<string>();
  for (const e of graph.edges) {
    if (pairs.has(`${e.source}|${e.target}`)) {
      keys.add(e.key);
    }
  }
  return keys;
}

/** A paidWith edge is a mismatch when the item's and the payment type's package ids differ (null counts as different). */
function isMismatch(e: GraphEdge, nodeByKey: Map<string, GraphNode>): boolean {
  const a = nodeByKey.get(e.source);
  const b = nodeByKey.get(e.target);
  const item = a?.type === 'item' ? a : b?.type === 'item' ? b : undefined;
  const pt = a?.type === 'paymentType' ? a : b?.type === 'paymentType' ? b : undefined;
  if (!item || !pt) {
    return true;
  }
  const itemPkg = (item.data as ItemNodeData).accountingPackageId;
  const ptPkg = (pt.data as PaymentTypeNodeData).accountingPackageId;
  return itemPkg === null || ptPkg === null || itemPkg !== ptPkg;
}

/** A defaultsTo edge is a mismatch when the payment type is a placeholder, or its own package is null or not the edge's package. */
function isDefaultsToMismatch(e: GraphEdge, nodeByKey: Map<string, GraphNode>): boolean {
  const a = nodeByKey.get(e.source);
  const b = nodeByKey.get(e.target);
  const pkg = a?.type === 'accountingPackage' ? a : b?.type === 'accountingPackage' ? b : undefined;
  const pt = a?.type === 'paymentType' ? a : b?.type === 'paymentType' ? b : undefined;
  if (!pkg || !pt || pt.flags?.isMissing) {
    return true;
  }
  const ptPkg = (pt.data as PaymentTypeNodeData).accountingPackageId;
  return ptPkg === null || ptPkg !== pkg.id;
}

export function toElements(
  graph: GatewayResolutionGraph,
  seedKeys: string[] = [],
  defaultPaths: string[][] = [],
): GraphElement[] {
  const degree = new Map<string, number>();
  for (const e of graph.edges) {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
  }
  const onPath = new Set([...graph.defaultPath, ...defaultPaths.flat()]);
  const seeds = new Set(seedKeys);
  const nodeByKey = new Map(graph.nodes.map((n) => [n.key, n] as [string, GraphNode]));
  const pathEdges = defaultPathEdgeKeys(graph, defaultPaths);

  const nodes: GraphElement[] = graph.nodes.map((n) => {
    const size = nodeSize(degree.get(n.key) ?? 0);
    const classes: string[] = [n.type];
    if (n.key === graph.start || seeds.has(n.key)) {
      classes.push('start');
    }
    if (onPath.has(n.key)) {
      classes.push('default-path');
    }
    if (n.flags?.isMissing) {
      classes.push('missing');
    }
    if (n.flags?.noDefaultPaymentType || n.flags?.noPackage) {
      classes.push('warning');
    }
    return {
      group: 'nodes',
      data: {
        id: n.key,
        type: n.type,
        ring: RING_LEVEL[n.type],
        label: n.label,
        glyph: NODE_GLYPH[n.type],
        size,
        glyphSize: Math.round(size * 0.55),
        ...n.data,
        entityId: n.id,
        ...(n.flags?.isMissing ? { isMissing: true } : {}),
        ...(n.flags?.noDefaultPaymentType ? { noDefaultPaymentType: true } : {}),
        ...(n.flags?.noPackage ? { noPackage: true } : {}),
      },
      classes: classes.join(' '),
    };
  });

  const edges: GraphElement[] = graph.edges.map((e) => {
    const classes: string[] = [e.kind];
    if (e.kind === 'paidWith' && isMismatch(e, nodeByKey)) {
      classes.push('mismatch');
    }
    if (e.kind === 'defaultsTo') {
      classes.push('defaultsTo');
      if (isDefaultsToMismatch(e, nodeByKey)) {
        classes.push('mismatch');
      }
    }
    if (pathEdges.has(e.key)) {
      classes.push('default-path');
    }
    return {
      group: 'edges',
      data: { id: e.key, source: e.source, target: e.target, kind: e.kind },
      classes: classes.join(' '),
    };
  });

  return [...nodes, ...edges];
}

export function neighbourSet(edges: GraphEdge[], key: string): { nodes: Set<string>; edges: Set<string> } {
  const nodes = new Set<string>([key]);
  const edgeKeys = new Set<string>();
  for (const e of edges) {
    if (e.source === key) {
      nodes.add(e.target);
      edgeKeys.add(e.key);
    } else if (e.target === key) {
      nodes.add(e.source);
      edgeKeys.add(e.key);
    }
  }
  return { nodes, edges: edgeKeys };
}

export function ringLayoutOptions() {
  return {
    name: 'concentric',
    concentric: (n: { data(k: string): unknown }) => Number(n.data('ring')),
    levelWidth: () => 1,
    boundingBox: { ...LAYOUT_BOX },
    fit: true,
    padding: 80,
    avoidOverlap: true,
    minNodeSpacing: 12,
    equidistant: false,
    startAngle: (3 / 2) * Math.PI,
    animate: false,
  };
}

export function computeRingGeometry(
  positions: { ring: RingType; x: number; y: number }[],
  centre: { x: number; y: number } = LAYOUT_CENTRE,
): { ring: RingType; radius: number }[] {
  const result: { ring: RingType; radius: number }[] = [];
  for (const ring of RING_ORDER) {
    const members = positions.filter((p) => p.ring === ring);
    if (members.length === 0) {
      continue;
    }
    const radius = members.reduce((s, p) => s + Math.hypot(p.x - centre.x, p.y - centre.y), 0) / members.length;
    if (radius >= 1) {
      result.push({ ring, radius });
    }
  }
  return result;
}

export interface RenderedGuide {
  ring: RingType;
  cx: number;
  cy: number;
  r: number;
  labelKey: string;
  labelX: number;
  labelY: number;
}

export function toRenderedGuides(
  geometry: { ring: RingType; radius: number }[],
  pan: { x: number; y: number },
  zoom: number,
  centre: { x: number; y: number } = LAYOUT_CENTRE,
): RenderedGuide[] {
  return geometry.map((g) => {
    const cx = pan.x + zoom * centre.x;
    const cy = pan.y + zoom * centre.y;
    const r = zoom * g.radius;
    return { ring: g.ring, cx, cy, r, labelKey: RING_LABEL_KEYS[g.ring], labelX: cx, labelY: cy - r - 6 };
  });
}
